import { useEffect, useState } from 'react'
import { authStorage } from 'app/auth-storage'
import { Pto } from '@outcoreaix/pto'
import { useGetCertificateStatusQuery } from 'api/learn-worlds-api'
import { useWorkspace } from './useWorkspace'
import { useGetCurrentSpaceMemberQuery } from 'api/spaces-api'
import { settings } from 'config/settings'

type Scope = 'space' | 'workspace' | null

const resolveScope = (workspaceId?: number | undefined, spaceId?: number | undefined): Scope => {
  if (spaceId) {
    return 'space'
  }

  if (workspaceId) {
    return 'workspace'
  }

  return null
}

const useStaffboarding = (
  workspaceId: number | undefined,
  spaceId: number | undefined,
  skip: boolean = false
): { onboardingNeeded: boolean } => {
  const scope = resolveScope(workspaceId, spaceId)
  const [cpdNoticeNeeded, setCpdNoticeNeeded] = useState<boolean | null>(null)

  const { currentWorkspace } = useWorkspace()
  const { data: currentMember, isLoading: memberLoading } = useGetCurrentSpaceMemberQuery(
    { spaceId: spaceId! },
    { skip: !authStorage.user || !spaceId || skip }
  )

  const { data: cpdStatusData, isLoading: cpdStatusLoading } = useGetCertificateStatusQuery(undefined, {
    skip: !authStorage.user || skip || !scope
  })

  // compute config once all data is ready
  useEffect(() => {
    if (!currentWorkspace?.currentMember || cpdStatusLoading || memberLoading) return

    let roleIsStaff = false
    if (scope === 'space') {
      roleIsStaff = currentMember?.role === Pto.Spaces.SpaceMemberRole.Staff
    } else {
      roleIsStaff = currentWorkspace.currentMember.role === Pto.Workspaces.WorkspaceRole.Staff
    }

    if (roleIsStaff) {
      setCpdNoticeNeeded(cpdStatusData ? !cpdStatusData.completed : true)
    }
  }, [currentWorkspace?.currentMember, currentMember, cpdStatusData, cpdStatusLoading, cpdNoticeNeeded, scope])

  const onboardingNeeded =
    !!scope &&
    cpdNoticeNeeded === true &&
    !authStorage.user?.email.endsWith(settings.systemSideDomain) &&
    (currentWorkspace?.forceCpdCompletion || false)

  return {
    onboardingNeeded
  }
}

export default useStaffboarding
